import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, MessageBar, MessageBarActions, MessageBarBody, Spinner, Subtitle2 } from "@fluentui/react-components";
import type { AccountInfo } from "@azure/msal-browser";
import { api, ApiError, stopViewAs, viewingAs, type Me } from "./api";
import { IN_TEAMS, initAuth, signIn, signOut } from "./auth";
import { MOCK } from "./config";
import { useResizableColumns } from "./columns";
import { watchIdle } from "./idle";
import { href, useRoute } from "./router";
import { Access } from "./screens/Access";
import { Actuals } from "./screens/Actuals";
import { BonusDashboard } from "./screens/BonusDashboard";
import { Books, CALLBACK_KEY, CALLBACK_PATH, finishCallback } from "./screens/Books";
import { Capacity } from "./screens/Capacity";
import { Cash } from "./screens/Cash";
import { Reports } from "./screens/Reports";
import { CategoryIndex } from "./screens/CategoryIndex";
import { Aliases, Clients } from "./screens/Clients";
import { Home } from "./screens/Home";
import { ImportActuals } from "./screens/ImportActuals";
import { ImportPlan } from "./screens/ImportPlan";
import { PeopleList } from "./screens/PeopleList";
import { Performance } from "./screens/Performance";
import { TimeOff } from "./screens/TimeOff";
import { PersonCard } from "./screens/PersonCard";
import { ProjectEditor } from "./screens/ProjectEditor";
import { ProjectsList } from "./screens/ProjectsList";
import { Profitability } from "./screens/Profitability";
import { Expenses } from "./screens/Expenses";
import { Settings } from "./screens/Settings";
import { MeContext } from "./ui";

type State =
  | { kind: "loading" }
  | { kind: "signed-out"; idle: boolean }
  | { kind: "no-access"; account: AccountInfo }
  | { kind: "error"; message: string; viewingAs?: boolean }
  | { kind: "ready"; account: AccountInfo; me: Me };

const IDLE_FLAG = "finance.signedOutIdle";

/** Screens arrive slice by slice; each shows only to those whose access level allows it. */
// Three sections (Chris, 2026-10-15): everyday screens first, then Business Management, then Admin.
type Section = "main" | "business" | "admin";
const SECTION_LABEL: Record<Exclude<Section, "main">, string> = { business: "Business Management", admin: "Admin" };
// `needs` is any one of; `needsAll`, when set, is every one of.
// Meetings and Actions retired with Meeting Ops (Chris, 2026-11-03). Screens, API and data are
// kept: see meeting-ops/SHUTDOWN.md to bring them back.
const SCREENS: { key: string; label: string; needs: string[] | null; needsAll?: string[]; phase?: string; section: Section }[] = [
  { key: "", label: "Home", needs: null, section: "main" },
  { key: "actuals", label: "Time Keeping", needs: ["plans.read", "period.close", "self.read"], section: "main" },
  { key: "expenses", label: "Expense reports", needs: ["expenses.self", "expenses.approve", "expenses.read"], section: "main" },
  { key: "timeoff", label: "Time off", needs: ["self.read", "people.edit"], section: "main" },
  { key: "bonus", label: "Bonus", needs: ["comp.read"], section: "business" },
  { key: "cash", label: "Cash", needs: null, needsAll: ["plans.read", "financials.read", "comp.read"], section: "business" },
  { key: "capacity", label: "Capacity", needs: ["plans.read"], section: "business" },
  { key: "clients", label: "Clients", needs: ["plans.read", "aliases.read"], section: "business" },
  { key: "people", label: "People", needs: ["comp.read"], section: "business" },
  { key: "performance", label: "Performance", needs: ["plans.read"], section: "business" },
  { key: "profitability", label: "Profitability", needs: null, needsAll: ["plans.read", "financials.read", "comp.read"], section: "business" },
  { key: "projects", label: "Projects", needs: ["plans.read"], section: "business" },
  { key: "reports", label: "Reports", needs: ["books.read"], section: "business" },
  { key: "access", label: "Access", needs: ["access.admin"], section: "admin" },
  { key: "aliases", label: "Client aliases", needs: ["aliases.read"], section: "admin" },
  { key: "books", label: "QuickBooks", needs: ["books.read"], section: "admin" },
  { key: "settings", label: "Settings", needs: ["people.edit", "tax.admin", "comp.read", "financials.read"], section: "admin" },
];

export function App() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    (async () => {
      try {
        const account = await initAuth();
        if (!account) {
          const idle = sessionStorage.getItem(IDLE_FLAG) === "1";
          sessionStorage.removeItem(IDLE_FLAG);
          return setState({ kind: "signed-out", idle });
        }
        const me = await api<Me>("/me");
        setState({ kind: "ready", account, me });
      } catch (err) {
        // Viewing as someone who can't be viewed any more: say why, and offer the way back.
        const v = viewingAs();
        if (v) return setState({ kind: "error", message: `Couldn't view Base Camp as ${v.name}: ${err instanceof Error ? err.message : "something went wrong."}`, viewingAs: true });
        if (err instanceof ApiError && err.status === 403) {
          const account = (await initAuth())!;
          return setState({ kind: "no-access", account });
        }
        setState({ kind: "error", message: err instanceof Error ? err.message : "Something went wrong." });
      }
    })();
  }, []);

  useEffect(() => {
    if (state.kind !== "ready" || MOCK) return;
    return watchIdle(() => {
      // Teams keeps its own session, so idle there hides the screens until the person comes back.
      if (IN_TEAMS) return setState({ kind: "signed-out", idle: true });
      sessionStorage.setItem(IDLE_FLAG, "1");
      void signOut();
    });
  }, [state.kind]);

  return (
    <div className="shell">
      <header className="masthead">
        <img src="/wordmark-black.png" alt="3Seven" className="wordmark" />
        <span className="product">Base Camp</span>
        {MOCK && (
          <Badge appearance="filled" color="warning">
            Mock mode: synthetic data
          </Badge>
        )}
        {state.kind === "ready" || state.kind === "no-access" ? (
          <span className="account">
            <Caption1>{state.account.name ?? state.account.username}</Caption1>
            {!IN_TEAMS && (
              <Button size="small" appearance="subtle" onClick={() => void signOut()}>
                Sign out
              </Button>
            )}
          </span>
        ) : null}
      </header>
      <main className="content">{body(state)}</main>
    </div>
  );
}

function body(state: State) {
  switch (state.kind) {
    case "loading":
      return <Spinner label="Signing in…" />;
    case "signed-out":
      return (
        <Card className="panel">
          <Subtitle2 className="statement">Sign in with your 3Seven account to continue.</Subtitle2>
          {state.idle && (
            <MessageBar intent="info">
              <MessageBarBody>You were signed out after 30 minutes without activity.</MessageBarBody>
            </MessageBar>
          )}
          <Button appearance="primary" onClick={() => void signIn()}>
            {IN_TEAMS ? "Continue" : "Sign in"}
          </Button>
        </Card>
      );
    case "no-access":
      return (
        <Card className="panel">
          <Subtitle2 className="statement">Your account has no access level in Base Camp.</Subtitle2>
          <Body1>Ask Chris Carlson to add you to a group that has one.</Body1>
        </Card>
      );
    case "error":
      return (
        <MessageBar intent="error">
          <MessageBarBody>{state.message}</MessageBarBody>
          {state.viewingAs && (
            <MessageBarActions>
              <Button onClick={() => void stopViewAs()}>Back to my view</Button>
            </MessageBarActions>
          )}
        </MessageBar>
      );
    case "ready":
      return (
        <MeContext.Provider value={state.me}>
          {state.me.viewingAs && <ViewingAsBanner me={state.me} />}
          <Signed me={state.me} />
        </MeContext.Provider>
      );
  }
}

/** While an admin views as someone else: whom, that it is read-only, and the way back. */
function ViewingAsBanner({ me }: { me: Me }) {
  const [busy, setBusy] = useState(false);
  const v = me.viewingAs!;
  const levels = me.levels.map((l) => l.name).join(", ");
  return (
    <MessageBar intent="warning" className="view-as-banner">
      <MessageBarBody>
        <strong>Viewing as {v.kind === "level" ? `the ${v.name} level` : v.levelName ? `${v.name} at the ${v.levelName} level` : v.name}</strong>
        {v.kind === "person" && !v.levelName && levels ? ` (${levels})` : ""}. You see what they see, read-only: nothing can be saved until you stop.
      </MessageBarBody>
      <MessageBarActions>
        <Button
          appearance="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void stopViewAs();
          }}
        >
          {busy ? "Stopping…" : "Stop viewing as"}
        </Button>
      </MessageBarActions>
    </MessageBar>
  );
}

const TIMEKEEPERS_HIDDEN = new Set([""]);

// Intuit returns to /quickbooks-callback with its answer in the query (QB1). It's kept for this tab
// only, the address is put back before sign-in can read it, and it's used once and cleared.
if (window.location.pathname === CALLBACK_PATH) {
  try {
    sessionStorage.setItem(CALLBACK_KEY, window.location.search);
  } catch {
    // Without session storage the admin connects again; nothing else depends on it.
  }
  window.history.replaceState(null, "", "/#/books/connection");
}

function useQuickBooksReturn(me: Me) {
  const [note, setNote] = useState<{ ok: boolean; text: string }>();
  useEffect(() => {
    let search: string | null = null;
    try {
      search = sessionStorage.getItem(CALLBACK_KEY);
      sessionStorage.removeItem(CALLBACK_KEY);
    } catch {
      search = null;
    }
    if (!search || !me.capabilities.includes("books.read")) return;
    void finishCallback(search).then((error) => {
      setNote(error ? { ok: false, text: error } : { ok: true, text: "QuickBooks is connected. Read its history next." });
    });
  }, [me]);
  return note;
}

function Signed({ me }: { me: Me }) {
  const route = useRoute();
  const booksNote = useQuickBooksReturn(me);
  // Timekeepers (nothing beyond self.read and actions.self) see only Time Keeping and Time off for now,
  // while time-off balances and taxes are settled (Chris, 2026-10-20). The API refuses Home to them too.
  const timekeeperOnly = me.capabilities.every((c) => c === "self.read" || c === "actions.self" || c === "expenses.self");
  const visible = SCREENS.filter(
    (s) =>
      (s.needs === null || s.needs.some((n) => me.capabilities.includes(n))) &&
      (!s.needsAll || (s.needsAll.every((n) => me.capabilities.includes(n)) && me.scope === "all")) &&
      !(timekeeperOnly && TIMEKEEPERS_HIDDEN.has(s.key)) &&
      // Expense reports show once their receipt storage exists (PRD ER5).
      (s.key !== "expenses" || !!me.features?.expenses),
  );
  const current = visible.find((s) => s.key === (route[0] ?? "") && !s.phase) ?? visible[0];
  const main = useRef<HTMLDivElement>(null);
  useResizableColumns(useCallback(() => main.current, []), current.key || "home");
  return (
    <div className="layout">
      <nav className="nav" aria-label="Screens">
        {visible.map((s, i) => [
          s.section !== "main" && visible[i - 1]?.section !== s.section ? (
            <div key={`${s.section}-heading`} className="nav-section" role="presentation">
              {SECTION_LABEL[s.section]}
            </div>
          ) : null,
          s.phase ? (
            <span key={s.key} className="nav-item later" aria-disabled="true">
              {s.label}
              <Badge appearance="outline" size="small">
                {s.phase}
              </Badge>
            </span>
          ) : (
            <a key={s.key} href={href(...(s.key ? [s.key] : []))} className={s === current ? "nav-item current" : "nav-item"} aria-current={s === current ? "page" : undefined}>
              {s.label}
            </a>
          ),
        ])}
      </nav>
      <div className="main" ref={main}>
        {booksNote && current.key === "books" && (
          <MessageBar intent={booksNote.ok ? "success" : "error"}>
            <MessageBarBody>{booksNote.text}</MessageBarBody>
          </MessageBar>
        )}
        {/* A finished QuickBooks connection remounts the screen, so it reads the new status. */}
        <Fragment key={booksNote?.text ?? ""}>{screen(current.key, route)}</Fragment>
      </div>
    </div>
  );
}

function screen(key: string, route: string[]) {
  switch (key) {
    case "projects":
      if (route[1] === "import") return <ImportPlan />;
      return route[1] ? <ProjectEditor key={route[1]} id={route[1]} /> : <ProjectsList />;
    case "capacity":
      return <Capacity />;
    case "cash":
      return <Cash />;
    case "reports":
      return <Reports tab={route[1]} />;
    case "profitability":
      return <Profitability />;
    case "expenses":
      return <Expenses key={route[1] ?? ""} id={route[1]} />;
    case "actuals":
      return route[1] === "import" ? <ImportActuals /> : route[1] === "index" ? <CategoryIndex /> : <Actuals tab={route[1]} />;
    case "performance":
      return <Performance />;
    case "timeoff":
      return <TimeOff tab={route[1]} />;
    case "access":
      return <Access />;
    case "clients":
      return <Clients key={route[1] ?? ""} id={route[1]} />;
    case "aliases":
      return <Aliases />;
    case "books":
      return <Books key={route[1] ?? ""} tab={route[1]} />;
    case "people":
      return route[1] ? <PersonCard key={route[1]} id={route[1]} /> : <PeopleList />;
    case "bonus":
      return <BonusDashboard />;
    case "settings":
      return <Settings tab={route[1]} />;
    default:
      return <Home />;
  }
}
